import _ from "lodash";
import React from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import { connect } from "react-redux";
import Select from 'react-select';
import { defaultAuths, getAppName, isDemo, userAllowedBackends } from "../../../environments";
import { getAreConcurrentUploadsEnabled, getIsFocusToAllocatedRoiAreaEnabled, getTinyContourFragmentsDrawMode, setAreConcurrentUploadsEnabled, setIsFocusToAllocatedRoiAreaEnabled, setTinyContourFragmentsDrawMode, setWindowLevelPresets, TinyContourFragmentsDrawMode } from "../../../local-storage";
import { DefaultWindowLevelPresets, getWindowLevelPresets } from "../../../rtviewer-core/window-level";

import * as sagas from '../../../store/sagas';
import { StoreState } from "../../../store/store";
import { User } from "../../../store/user";
import AppAuth from "../../../web-apis/app-auth";
import { backends, backendTierAppAuths } from "../../../web-apis/auth";
import { Backend } from "../../../web-apis/backends";
import { Checkbox } from "../../misc-components";
import ModalDialog from "../ModalDialog";


import './UserSettingsDialog.css';

type OwnProps = {}

type DispatchProps = {
    setUserSettingsDialogVisibility(value: boolean): void,
    setUserSettingBackend(backend: Backend | null): void,
    logIntoAppAuth: (appAuth: AppAuth) => void,
    requestLogOut: () => void,
    setUserSettingPatientInfoVisibility(showPatientInfo: boolean): void,
}

type OwnState = {
    /** This state prop is only used to get the settings react component to self-update.
     * The actual value is read from and written to localStorage. */
    areConcurrentUploadsEnabledHack: boolean,
    isFocusToAllocatedRoiAreaEnabledHack: boolean,
    tinyContourFragmentsDrawModeHack: TinyContourFragmentsDrawMode,
    confirmAction: 'reset' | 'merge' | null,
}

type AllProps = OwnProps & StoreState & DispatchProps;

const tinyContourFragmentsDrawModeOptions = [
    { label: 'Always', value: TinyContourFragmentsDrawMode.Always },
    { label: 'When viewing stray contours', value: TinyContourFragmentsDrawMode.WhenViewingStrayContours },
    { label: 'Never', value: TinyContourFragmentsDrawMode.Never },
];

class UserSettingsDialog extends React.Component<AllProps, OwnState> {

    constructor(props: AllProps) {
        super(props);

        this.state = {
            areConcurrentUploadsEnabledHack: getAreConcurrentUploadsEnabled(),
            isFocusToAllocatedRoiAreaEnabledHack: getIsFocusToAllocatedRoiAreaEnabled(),
            tinyContourFragmentsDrawModeHack: getTinyContourFragmentsDrawMode(),
            confirmAction: null,
        };
    }

    isDefaultAppAuth = (appAuth: AppAuth) => {
        return defaultAuths.includes(appAuth.appName);
    }

    handleClose = () => {
        this.props.setUserSettingsDialogVisibility(false);
    }

    handleChange = (option: any) => {
        this.props.setUserSettingBackend(backends.getBackend(option.value));
    }

    handleLogOutClick = () => {
        this.props.requestLogOut();
    }

    handleLogInClick = (appAuth: AppAuth) => {
        this.props.logIntoAppAuth(appAuth);
    }

    handleUploadConcurrencyChange = () => {
        const newValue = !getAreConcurrentUploadsEnabled();
        setAreConcurrentUploadsEnabled(newValue);
        this.setState({ areConcurrentUploadsEnabledHack: newValue });
    }

    handleFocusToAllocatedRoiAreaChange = () => {
        const newValue = !getIsFocusToAllocatedRoiAreaEnabled();
        setIsFocusToAllocatedRoiAreaEnabled(newValue);
        this.setState({ isFocusToAllocatedRoiAreaEnabledHack: newValue });
    }

    handleTinyContourFragmentsDrawModeChange = (option: any) => {
        setTinyContourFragmentsDrawMode(option.value);
        this.setState({ tinyContourFragmentsDrawModeHack: option.value });
    }
    
    handleResetWindowingPresets = () => {
        setWindowLevelPresets({ ...DefaultWindowLevelPresets });
        this.setState({ confirmAction: null });
    }

    handleMergeWindowingPresets = () => {
        const current = getWindowLevelPresets();
        Object.keys(DefaultWindowLevelPresets).forEach(key => {
            if (!current[key]) {
                current[key] = DefaultWindowLevelPresets[key];
            }
        });
        setWindowLevelPresets(current);
        this.setState({ confirmAction: null });
    }

    handleShowPatientInfoChange = (newValue: boolean) => {
        this.props.setUserSettingPatientInfoVisibility(newValue);
    }

    render() {
        const isVisible = this.props.isUserSettingsDialogVisible;

        const options = userAllowedBackends.map(b => ({ label: b.name, value: backends.getBackend(b.name).name }));
        const user = this.props.user as User;
        const permissions = user.permissions;
        const currentBackend = user.currentBackend !== null ? user.currentBackend.name : backends.getDefaultBackend().name;

        const canChangeBackend = !isDemo() && permissions.canChangeBackend;
        const showAppRegistrations = !isDemo() && permissions.allowDebugMode;

        const areConcurrentUploadsEnabled = getAreConcurrentUploadsEnabled();
        const isFocusToAllocatedRoiAreaEnabled = getIsFocusToAllocatedRoiAreaEnabled();
        const tinyContourFragmentsDrawMode = getTinyContourFragmentsDrawMode();

        return (<>
            <ModalDialog
                show={isVisible}
                onHide={this.handleClose}
                size="lg">

                <Modal.Header closeButton>
                    <Modal.Title>User Session Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body className="user-settings">
                    <div className="notice">Note that these settings are only active in this session/tab.</div>
                    <Form>
                        <Form.Group>
                            <Form.Row>
                                <Col lg={6}>
                                    <div>Show identifying information (patient ID, dataset name) on main view and splash screen</div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Checkbox
                                        isSelected={user.showPatientInfo}
                                        onCheckboxChange={() => this.handleShowPatientInfoChange(!user.showPatientInfo)}
                                    />
                                </Col>
                            </Form.Row>
                        </Form.Group>
                        {canChangeBackend && (
                            <Form.Group>
                                <Form.Row>
                                    <Col lg={6}>
                                        <div>Current backend</div>
                                    </Col>
                                    <Col className="value-row-shift-dropdown">
                                        <Select
                                            options={options}
                                            onChange={this.handleChange}
                                            value={options.find(opt => currentBackend && opt.value === currentBackend)}>
                                        </Select>
                                    </Col>
                                </Form.Row>
                            </Form.Group>
                        )}
                        {showAppRegistrations && (
                            <Form.Group>
                                <Form.Label>Backend/API auth debug information:</Form.Label>
                                {_.map(backendTierAppAuths, (appAuth, tier) => (
                                    <Form.Row className="app-auth-row" key={tier}>
                                        <Col lg={3}>
                                            <div>Tier: {tier}</div>
                                        </Col>
                                        <Col lg={3}>
                                            API: {appAuth.appName}
                                        </Col>
                                        <Col lg={3}>
                                            {appAuth.isLoggedIn ? 'Logged in' : 'Not logged in'}
                                        </Col>
                                        <Col lg={3}>
                                            {!appAuth.isLoggedIn && (<Button size="sm" variant={this.isDefaultAppAuth(appAuth) ? 'primary' : 'secondary'} onClick={() => this.handleLogInClick(appAuth)}>Log in</Button>)}
                                        </Col>
                                    </Form.Row>
                                ))}
                            </Form.Group>
                        )}

                        <hr />

                        <Form.Group>
                            <h3>Local settings</h3>

                            <div className="notice">
                                These settings apply to every session/tab of {getAppName()} in this browser.
                            </div>

                            <Form.Row>
                                <Col lg={6}>
                                    <div title="Disabling this option will limit file uploads to one at a time.">
                                        Enable concurrent uploads
                                        <div className="setting-notice">Applied after you refresh this tab.</div>
                                    </div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Checkbox
                                        label=""
                                        isSelected={areConcurrentUploadsEnabled}
                                        onCheckboxChange={this.handleUploadConcurrencyChange}
                                    />
                                </Col>
                            </Form.Row>

                            <Form.Row>
                                <Col lg={6}>
                                    <div title="Old behaviour: focus on the whole area allocated for the structure. It can be larger than the visible contours, e.g. after contours have been cleared or erased. When unchecked, focus is on the visible contours.">
                                        Focus on structure uses allocated structure area (legacy)
                                    </div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Checkbox
                                        label=""
                                        isSelected={isFocusToAllocatedRoiAreaEnabled}
                                        onCheckboxChange={this.handleFocusToAllocatedRoiAreaChange}
                                    />
                                </Col>
                            </Form.Row>

                            <Form.Row>
                                <Col lg={6}>
                                    <div title="Contour fragments smaller than a pixel can be invisible. They can be drawn as filled squares of at least one image pixel so that they can be spotted and removed. At some zoom levels the squares can hide the contours under them.">
                                        Draw tiny contour fragments (at least one pixel)
                                    </div>
                                </Col>
                                <Col className="value-row-shift-dropdown">
                                    <Select
                                        options={tinyContourFragmentsDrawModeOptions}
                                        onChange={this.handleTinyContourFragmentsDrawModeChange}
                                        value={tinyContourFragmentsDrawModeOptions.find(opt => opt.value === tinyContourFragmentsDrawMode)}>
                                    </Select>
                                </Col>
                            </Form.Row>

                            <Form.Row className="windowing-presets-section">
                                <Col lg={6}>
                                    <div>Windowing presets</div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                     <Button variant="outline-danger" className="mr-2" onClick={() => this.setState({ confirmAction: 'reset' })}>
                                        Reset windowing presets to defaults
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => this.setState({ confirmAction: 'merge' })}>
                                        Merge default windowing presets
                                    </Button>
                                </Col>
                            </Form.Row>
                        </Form.Group>

                        <Form.Group>
                            <Form.Row className="logout-button">
                                <Col lg={6}>
                                    <div>Logged in as <b><span title={user.email}>{user.username}</span></b></div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Button variant="danger" onClick={() => this.handleLogOutClick()}>Log out of {getAppName()}</Button>
                                </Col>
                            </Form.Row>
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={this.handleClose}>Ok</Button>
                </Modal.Footer>
            </ModalDialog>

            <ModalDialog
                show={this.state.confirmAction !== null}
                onHide={() => this.setState({ confirmAction: null })}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {this.state.confirmAction === 'reset' ? 'Reset windowing presets' : 'Merge windowing presets'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.state.confirmAction === 'reset'
                        ? 'This will replace all your custom windowing presets with the defaults. This action cannot be undone.'
                        : 'This will add any missing default windowing presets to your current set. Existing presets with the same name will be kept unchanged.'}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => this.setState({ confirmAction: null })}>Cancel</Button>
                    <Button
                        variant={this.state.confirmAction === 'reset' ? 'danger' : 'primary'}
                        onClick={this.state.confirmAction === 'reset' ? this.handleResetWindowingPresets : this.handleMergeWindowingPresets}
                    >
                        {this.state.confirmAction === 'reset' ? 'Reset' : 'Merge'}
                    </Button>
                </Modal.Footer>
            </ModalDialog>
        </>);
    }
}

export default connect(
    state => Object.assign({}, state),
    sagas.mapDispatchToProps,
)(UserSettingsDialog);
